/**
 * Type definitions for decision-ui components
 * These mirror the core engine types but are UI-focused
 */

/**
 * Confidence summary showing reliability of recommendation
 */
export interface ConfidenceSummary {
  level: "low" | "medium" | "high";
  reasons: string[];  // max 3 bullets explaining the confidence level
  toImprove: Array<{
    key: string;      // e.g. "annual_kwh", "heat_loss", "pipework_layout"
    label: string;    // shown to user
    action: string;   // short instruction, brand-neutral
  }>;                 // max 3 actions to improve confidence
}

/**
 * Red flag warning for installation planning
 */
export interface RedFlag {
  id: string;           // stable id e.g. "low_mains_pressure"
  severity: "info" | "warning";
  title: string;
  detail: string;
  mitigation?: string;
}

/**
 * System losses and inefficiencies breakdown
 * Explains why fuel input is higher than delivered heat
 * 
 * Note: This type is duplicated from src/core/types.ts because the decision-ui
 * package is compiled independently and cannot import from @core. The types
 * must be kept in sync manually.
 */
export interface LossesBreakdown {
  cyclingAndControlsKwh?: number;   // Losses from cycling, oversizing, and control behavior
  distributionKwh?: number;         // Pipework and distribution losses
  cylinderStandingKwh?: number;     // Cylinder standing losses (if applicable)
  keepWarmKwh?: number;             // Keep-warm / preheat energy (if applicable)
  otherKwh?: number;                // Other system inefficiencies
}

export interface BaselineKPIData {
  annualCost_gbp: number | null;
  totalCost_gbp: number | null;
  periodYears: number;
  remainingLifespan_years: { min: number; max: number } | null;
  expectedReplacements: number | null;
  efficiencySource: string;
  sludgeRiskApplied: boolean;
  sludgeRiskLevel?: string;
}

export interface SystemTypeCardData {
  id: string;
  title: string;
  systemType: string;
  suitability: 'high' | 'medium' | 'low';
  reasons: string[];
  constraints: string[];
  longevity: { min: number; max: number };
  replacements: number;
}

export interface DecisionTableRow {
  optionName: string;
  lifespan: string;
  replacements: number;
  annualCost_gbp: number | null;
  totalCost_gbp: number | null;
  deltaVsBaseline_gbp: number;
  isBaseline?: boolean;
}

export interface CumulativeCostDataPoint {
  year: number;
  baseline?: number;
  [key: string]: number | undefined;
}

export interface AnnualCostBarData {
  label: string;
  cost: number;
  delta?: number;
  isBaseline?: boolean;
}

export interface ChartColors {
  baseline: string;
  primary: string;
  alternatives: string[];
}

export const defaultColors: ChartColors = {
  baseline: '#94a3b8',
  primary: '#3b82f6',
  alternatives: ['#10b981', '#f59e0b', '#ef4444'],
};

/**
 * ============================================================================
 * Additional types mirrored from src/core/types.ts
 * ============================================================================
 * These types are duplicated here because the decision-ui package is compiled
 * independently and cannot import from @core. The types must be kept in sync
 * manually with the source definitions in src/core/types.ts.
 */

export type Confidence = "low" | "medium" | "high";

export type ImprovementEligibility = 'eligible' | 'already_present' | 'not_applicable' | 'unknown';

/**
 * A single optional improvement
 */
export interface OptionalImprovement {
  /** Improvement identifier */
  id: 'adaptive_controls' | 'clean_sealed' | 'distribution_enabling';
  /** Display name */
  name: string;
  /** Description of what this improvement does */
  description: string;
  /** Eligibility status */
  eligibility: ImprovementEligibility;
  /** Why this improvement is or isn't eligible */
  eligibilityReason: string;
  /** Typical impact range (percentage of delivered space heat) */
  impactRange: {
    min: number;
    max: number;
  };
  /** Impact units description */
  impactDescription: string;
  /** Whether to show percentage claim (false for distribution/enabling) */
  showPercentage: boolean;
  /** Additional notes or caveats */
  notes?: string;
  /** Estimated cost range */
  costRange?: {
    min: number;
    max: number;
  };
  /** Typical payback period in years */
  paybackYears?: {
    min: number;
    max: number;
  };
}

/**
 * Complete optional improvements data for an option
 */
export interface OptionalImprovementsData {
  /** Option this relates to */
  optionId: string;
  /** The improvements */
  improvements: OptionalImprovement[];
  /** Baseline label for comparison */
  baselineLabel: 'current_system' | 'modern_gas_equivalent';
  /** Header copy (required by acceptance criteria) */
  headerCopy: string;
}

/**
 * Seasonal COP assumptions for a scenario
 */
export interface SCOPAssumptions {
  /** Design flow temperature */
  flowTempC: number;
  /** SCOP range (low end of typical) */
  scopMin: number;
  /** SCOP range (high end of typical) */
  scopMax: number;
  /** Confidence in these assumptions */
  confidence: Confidence;
  /** Explanation of assumptions */
  assumptions: string[];
}

/**
 * A single heat pump scenario
 */
export interface HeatPumpScenario {
  /** Scenario identifier */
  id: 'as_is' | 'low_temp_optimised';
  /** Display label */
  label: string;
  /** Description of what this scenario assumes */
  description: string;
  /** What changes from current state */
  changes: string[];
  /** Delivered heat (kept constant across scenarios) */
  deliveredHeatKwh: {
    spaceHeating: number;
    hotWater: number;
    total: number;
  };
  /** SCOP assumptions for this scenario */
  scop: SCOPAssumptions;
  /** Annual electricity consumption */
  electricityKwh: number;
  /** Annual running cost estimate */
  annualCostGbp: {
    estimate: number;
    confidence: Confidence;
  };
  /** Annual carbon emissions */
  carbonKg?: number;
}

/**
 * Complete dual heat pump scenarios data
 */
export interface DualHeatPumpScenariosData {
  /** Option this relates to */
  optionId: string;
  /** Option display name */
  optionLabel: string;
  /** The two scenarios */
  asIs: HeatPumpScenario;
  lowTempOptimised: HeatPumpScenario;
  /** Whether low-temp optimised is feasible */
  lowTempFeasible: boolean;
  /** Why low-temp may not be feasible */
  feasibilityNote?: string;
  /** Metadata */
  generated: {
    timestamp: string;
    rulesetVersion: string;
  };
}

/**
 * A single step in the weighing animation
 */
export interface WeighingStep {
  /** Step identifier */
  id: string;
  /** Display label for the step */
  label: string;
  /** Longer description of what this step considers */
  description: string;
  /** Whether this step has data available (vs assumed typical) */
  hasData: boolean;
  /** Fallback note if data unavailable */
  fallbackNote?: string;
  /** Weight contribution (0-1 scale, for visual token size) */
  weight: number;
  /** Impact on final result (positive = favors this option, negative = against) */
  impact: 'positive' | 'neutral' | 'negative';
}

/**
 * Complete data for the weighing animation
 */
export interface WeighingAnimationData {
  /** Option being weighed */
  optionId: string;
  /** Option display name */
  optionLabel: string;
  /** The steps shown in the animation */
  steps: WeighingStep[];
  /** Final results shown after animation completes */
  results: {
    /** Annual running cost */
    annualCostGbp: number;
    /** Annual carbon emissions (optional toggle) */
    carbonKg?: number;
    /** Confidence level */
    confidence: Confidence;
  };
  /** Metadata */
  generated: {
    timestamp: string;
    rulesetVersion: string;
  };
}

/**
 * Annual energy use input
 */
export interface AnnualEnergyInput {
  /** Preferred: kWh usage */
  annualKwh?: number;
  /** Fallback: annual spend */
  annualSpendGbp?: number;
  /** Tariff for conversion (if spend provided) */
  tariffPencePerKwh?: number;
  /** Source of this data */
  source: 'meter' | 'bill' | 'estimate' | 'unknown';
}

/**
 * Heat loss input
 */
export interface HeatLossInput {
  /** Whole-house heat loss in kW */
  wholeHouseKw?: number;
  /** Fast-capture threshold band used when exact kW is not available */
  thresholdBand?: 'below_19kw' | 'at_or_above_19kw' | 'unknown';
  /** Room-by-room data (optional, for future expansion) */
  roomByRoom?: Array<{
    roomName: string;
    heatLossKw: number;
  }>;
  /** Source of this data */
  source: 'calculated' | 'measured' | 'estimate' | 'unknown';
}

/**
 * Distribution system input
 */
export interface DistributionInput {
  /** Persistent cold radiators */
  persistentColdRads: 'none' | 'one' | 'several' | 'unknown';
  /** Primary pipe size */
  primaryPipeSize: '15mm' | '22mm' | '28mm' | 'unknown';
  /** System layout */
  systemLayout: 'y_plan' | 's_plan' | 'unknown';
  /** Pipework accessibility */
  pipeworkAccessibility: 'accessible' | 'in_screed' | 'mixed' | 'unknown';
}

/**
 * Complete data capture for real numbers
 */
export interface RealNumbersDataCapture {
  /** Annual energy use */
  energy?: AnnualEnergyInput;
  /** Heat loss data */
  heatLoss?: HeatLossInput;
  /** Distribution system data */
  distribution?: DistributionInput;
  /** Computed confidence boost from this data */
  confidenceBoost: number;
  /** Gates unlocked by this data */
  gatesUnlocked: string[];
}
